import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { JobSearchComboBox } from '@/components/shared/job-search-combo-box';
import type { JobOption } from '@/components/shared/job-search-combo-box';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Label } from '@/app/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import { FieldError } from '@/app/components/ui/field-error';
import {
  useCreateAndApproveSafeWorkPlan,
  useCreateSafeWorkPlan,
  useSafeWorkPlan,
  useSafeWorkPlans,
  useUpdateSafeWorkPlan,
} from '@/hooks/use-safety';
import { useOrgToday } from '@/hooks/use-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { OfflineIndicator } from '@/app/components/offline/offline-indicator';
import type { AccessMethod } from '@/lib/types';
import {
  fieldErrorMessage,
  parseFormErrors,
  rowErrorMessage,
  summarizeFormErrors,
  type FormErrors,
} from '@/lib/form-errors';
import { isExistingPlanConflict, validateWorkPlanForm } from '../plan-form-validation';

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  project: 'Project',
  location_fk: 'Location',
  location: 'Specific Area / Details',
  date: 'Date',
  jsa: 'JSA',
  access_method: 'Access Method',
  scope_of_work: 'Scope of Work',
  rigging_plan: 'Rigging Plan',
  equipment_requirements: 'Equipment Requirements',
  steps: 'Work step',
  anchor_points_description: 'Anchor Points Description',
  fall_distance_calculation: 'Fall Distance Calculation',
  exclusion_zones: 'Exclusion Zones',
  communication_plan: 'Communication Plan',
  weather_limitations: 'Weather Limitations',
};

interface StepRow {
  step_number: number;
  description: string;
  safety_considerations: string;
  responsible_role: string;
  order: number;
}

const accessMethods: { value: AccessMethod; label: string }[] = [
  { value: 'single_rope', label: 'Single Rope' },
  { value: 'double_rope', label: 'Double Rope' },
  { value: 'aid_climbing', label: 'Aid Climbing' },
  { value: 'bosuns_chair', label: "Bosun's Chair" },
  { value: 'scaffold', label: 'Scaffold' },
  { value: 'suspended_platform', label: 'Suspended Platform' },
  { value: 'other', label: 'Other' },
];

export function CreateWorkPlanPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const planId = id ? parseInt(id, 10) : 0;
  const isEditing = planId > 0;
  const createMutation = useCreateSafeWorkPlan();
  const createAndApproveMutation = useCreateAndApproveSafeWorkPlan();
  const updateMutation = useUpdateSafeWorkPlan();
  const { data: existingPlan } = useSafeWorkPlan(planId);
  const { hasMinModuleRole } = usePermissions();
  const canManageSafety = hasMinModuleRole('safety', 'manager');
  const orgToday = useOrgToday();
  const initializedFromPlan = useRef(false);

  const [title, setTitle] = useState('');
  const [selectedProject, setSelectedProject] = useState<JobOption | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [location, setLocation] = useState('');
  const [date, setDate] = useState(orgToday);
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [riggingPlan, setRiggingPlan] = useState('');
  const [accessMethod, setAccessMethod] = useState<AccessMethod>('single_rope');
  const [equipmentRequirements, setEquipmentRequirements] = useState('');
  const [anchorPoints, setAnchorPoints] = useState('');
  const [fallDistance, setFallDistance] = useState('');
  const [exclusionZones, setExclusionZones] = useState('');
  const [communicationPlan, setCommunicationPlan] = useState('');
  const [weatherLimitations, setWeatherLimitations] = useState('');
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors | null>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  // "This job already has a safe work plan" — look the current one up so the
  // message can link straight to it.
  const projectError = fieldErrorMessage(formErrors, 'project');
  const projectHasPlan = isExistingPlanConflict(formErrors?.fields.project);
  const { data: plansForProject } = useSafeWorkPlans(
    { project: selectedProject?.id, page_size: 10 },
    projectHasPlan && Boolean(selectedProject),
  );
  const conflictingPlan = plansForProject?.results
    .filter((plan) => plan.project === selectedProject?.id)
    .sort((a, b) => Number(b.is_current ?? false) - Number(a.is_current ?? false))[0];

  const showFormErrors = (errors: FormErrors) => {
    setFormErrors(errors);
    window.setTimeout(() => {
      const summary = errorSummaryRef.current;
      if (summary && typeof summary.scrollIntoView === 'function') {
        summary.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  };

  useEffect(() => {
    if (!existingPlan || initializedFromPlan.current) return;
    initializedFromPlan.current = true;
    setTitle(existingPlan.title);
    setSelectedProject(
      existingPlan.project && existingPlan.project_display
        ? {
            id: existingPlan.project,
            name: existingPlan.project_display.name,
            job_number: existingPlan.project_display.project_number,
            client_name: '',
            location_fk: existingPlan.location_fk,
            location_fk_display: existingPlan.location_fk_display,
          }
        : null
    );
    setSelectedLocation(existingPlan.location_fk_display);
    setLocation(existingPlan.location);
    setDate(existingPlan.date);
    setScopeOfWork(existingPlan.scope_of_work);
    setRiggingPlan(existingPlan.rigging_plan);
    setAccessMethod(existingPlan.access_method);
    setEquipmentRequirements(existingPlan.equipment_requirements);
    setAnchorPoints(existingPlan.anchor_points_description);
    setFallDistance(existingPlan.fall_distance_calculation);
    setExclusionZones(existingPlan.exclusion_zones);
    setCommunicationPlan(existingPlan.communication_plan);
    setWeatherLimitations(existingPlan.weather_limitations);
    setSteps(
      existingPlan.steps.map((step, index) => ({
        step_number: index + 1,
        description: step.description,
        safety_considerations: step.safety_considerations,
        responsible_role: step.responsible_role,
        order: index,
      }))
    );
  }, [existingPlan]);

  const addStep = () => {
    setSteps([
      ...steps,
      {
        step_number: steps.length + 1,
        description: '',
        safety_considerations: '',
        responsible_role: '',
        order: steps.length,
      },
    ]);
  };

  const removeStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const updateStep = (index: number, field: keyof StepRow, value: string) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], [field]: value };
    setSteps(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const submitter = (e.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement | null;
    const shouldCreateAndApprove = submitter?.value === 'create-and-approve';
    setFormErrors(null);

    // Catch what the backend would reject before sending anything, so the
    // problems show up next to the fields instead of a silent 400.
    const validationErrors = validateWorkPlanForm({ steps });
    if (validationErrors) {
      showFormErrors(validationErrors);
      return;
    }

    try {
      const payload = {
        title,
        jsa: existingPlan?.jsa ?? undefined,
        project: selectedProject?.id ?? null,
        project_name: selectedProject?.name || '',
        project_number: selectedProject?.job_number || '',
        location_fk: selectedLocation?.id || null,
        location,
        date,
        scope_of_work: scopeOfWork,
        rigging_plan: riggingPlan,
        access_method: accessMethod,
        equipment_requirements: equipmentRequirements,
        anchor_points_description: anchorPoints,
        fall_distance_calculation: fallDistance,
        exclusion_zones: exclusionZones,
        communication_plan: communicationPlan,
        weather_limitations: weatherLimitations,
        steps: steps.map((s, i) => ({ ...s, step_number: i + 1, order: i })),
      };
      const plan = isEditing
        ? await updateMutation.mutateAsync({ id: planId, data: payload })
        : shouldCreateAndApprove
          ? await createAndApproveMutation.mutateAsync(payload)
          : await createMutation.mutateAsync(payload);

      navigate(`/safety/work-plans/${plan.id}`);
    } catch (error) {
      showFormErrors(
        parseFormErrors(error, 'The safe work plan could not be saved. Please try again.'),
      );
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/safety/work-plans')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Safe Work Plan' : 'New Safe Work Plan'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Update this draft method statement' : 'Create a method statement for your project'}
          </p>
        </div>
      </div>

      <OfflineIndicator />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Rigging Plan - East Face Window Replacement"
                required
                aria-invalid={Boolean(fieldErrorMessage(formErrors, 'title'))}
              />
              <FieldError message={fieldErrorMessage(formErrors, 'title')} />
            </div>
            <div>
              <Label>Project</Label>
              <JobSearchComboBox
                value={selectedProject}
                onChange={(job) => {
                  setSelectedProject(job);
                  if (job?.location_fk_display && !selectedLocation) {
                    setSelectedLocation({
                      id: job.location_fk_display.id,
                      name: job.location_fk_display.name,
                      type: job.location_fk_display.type,
                    });
                  }
                }}
              />
              {projectError && (
                <FieldError message={projectError}>
                  {conflictingPlan && (
                    <>
                      {' '}
                      <Link
                        to={`/safety/work-plans/${conflictingPlan.id}`}
                        className="font-medium underline"
                      >
                        Open the existing safe work plan
                      </Link>
                    </>
                  )}
                </FieldError>
              )}
            </div>
            <div>
              <Label>Location</Label>
              <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Specific Area / Details</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. East face, 3rd floor scaffolding"
                />
              </div>
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
                <FieldError message={fieldErrorMessage(formErrors, 'date')} />
              </div>
            </div>
            <div>
              <Label htmlFor="accessMethod">Access Method</Label>
              <Select
                value={accessMethod}
                onValueChange={(value) => setAccessMethod(value as AccessMethod)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accessMethods.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Scope &amp; Rigging</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="scope">Scope of Work</Label>
              <Textarea
                id="scope"
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
                rows={3}
                placeholder="Detailed description of the work method..."
              />
            </div>
            <div>
              <Label htmlFor="rigging">Rigging Plan</Label>
              <Textarea
                id="rigging"
                value={riggingPlan}
                onChange={(e) => setRiggingPlan(e.target.value)}
                rows={3}
                placeholder="Anchor points, rope routes, rigging configuration..."
              />
            </div>
            <div>
              <Label htmlFor="equipment">Equipment Requirements</Label>
              <Textarea
                id="equipment"
                value={equipmentRequirements}
                onChange={(e) => setEquipmentRequirements(e.target.value)}
                rows={2}
                placeholder="Specific gear needed beyond standard PPE..."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Work Steps</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addStep}>
              <Plus className="h-4 w-4 mr-1" />
              Add Step
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <FieldError message={fieldErrorMessage(formErrors, 'steps')} />
            {steps.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No steps added yet. Click "Add Step" to begin.
              </p>
            ) : (
              steps.map((step, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Step #{index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeStep(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={step.description}
                      onChange={(e) => updateStep(index, 'description', e.target.value)}
                      rows={2}
                      placeholder="e.g., Rig the main and backup lines at anchor A"
                      aria-invalid={Boolean(rowErrorMessage(formErrors, 'steps', index, 'description'))}
                    />
                    <FieldError message={rowErrorMessage(formErrors, 'steps', index, 'description')} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Safety Considerations</Label>
                      <Input
                        value={step.safety_considerations}
                        onChange={(e) =>
                          updateStep(index, 'safety_considerations', e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <Label>Responsible Role</Label>
                      <Input
                        value={step.responsible_role}
                        onChange={(e) => updateStep(index, 'responsible_role', e.target.value)}
                        placeholder="e.g., Lead Tech"
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="anchor">Anchor Points Description</Label>
              <Textarea
                id="anchor"
                value={anchorPoints}
                onChange={(e) => setAnchorPoints(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="fall">Fall Distance Calculation</Label>
              <Textarea
                id="fall"
                value={fallDistance}
                onChange={(e) => setFallDistance(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="exclusion">Exclusion Zones</Label>
              <Textarea
                id="exclusion"
                value={exclusionZones}
                onChange={(e) => setExclusionZones(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="comms">Communication Plan</Label>
              <Textarea
                id="comms"
                value={communicationPlan}
                onChange={(e) => setCommunicationPlan(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="weather">Weather Limitations</Label>
              <Textarea
                id="weather"
                value={weatherLimitations}
                onChange={(e) => setWeatherLimitations(e.target.value)}
                rows={2}
                placeholder="Max wind speed, rain policy, temperature limits..."
              />
            </div>
          </CardContent>
        </Card>

        {formErrors && (
          <div ref={errorSummaryRef}>
            <Alert variant="destructive">
              <AlertTitle>
                {isEditing
                  ? 'The safe work plan could not be saved'
                  : 'The safe work plan could not be created'}
              </AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-5 space-y-1">
                  {summarizeFormErrors(formErrors, FIELD_LABELS).map((line, index) => (
                    <li key={`${index}-${line}`}>{line}</li>
                  ))}
                </ul>
                {conflictingPlan && (
                  <Link
                    to={`/safety/work-plans/${conflictingPlan.id}`}
                    className="font-medium underline"
                  >
                    Open the existing safe work plan
                  </Link>
                )}
              </AlertDescription>
            </Alert>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/safety/work-plans')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            value="create"
            disabled={
              createMutation.isPending ||
              createAndApproveMutation.isPending ||
              updateMutation.isPending
            }
          >
            {isEditing
              ? updateMutation.isPending
                ? 'Saving...'
                : 'Save Changes'
              : createMutation.isPending
                ? 'Creating...'
                : 'Create Work Plan'}
          </Button>
          {!isEditing && canManageSafety && (
            <Button
              type="submit"
              value="create-and-approve"
              disabled={createMutation.isPending || createAndApproveMutation.isPending}
            >
              {createAndApproveMutation.isPending ? 'Approving...' : 'Create & approve'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
